import {
  applicantSection,
  feeInfo,
  otherAttachments,
  proxyAttachments,
  replySection,
  representationSection,
} from "modules/ebiForms/common";
import { EEbiFormCategory, EEbiFormOffice, IEbiFormDefinition } from "modules/ebiForms/typings";

/**
 * A2. Wniosek o wydanie wypisu i wyrysu z MPZP / planu ogólnego — art. 30 ust. 1 upzp, § 8 rozporządzenia MRiT
 * z 8.12.2023 r.; opłata: część I pkt 51 załącznika do ustawy o opłacie skarbowej. Brak wzoru ogólnopolskiego; układ jak
 * wzór Ostrowa Wielkopolskiego (dane wnioskodawcy, dane nieruchomości, sposób odbioru, opłata naliczana po przygotowaniu).
 */
const A2: IEbiFormDefinition = {
  code: "A2",
  version: 1,
  title: "Wniosek o wydanie wypisu i wyrysu z planu miejscowego lub planu ogólnego",
  category: EEbiFormCategory.Planning,
  office: EEbiFormOffice.Municipality,
  description: "Wypis i wyrys z miejscowego planu zagospodarowania przestrzennego albo z planu ogólnego gminy dla wskazanych działek.",
  keywords: ["mpzp", "plan miejscowy", "plan ogólny", "wypis", "wyrys", "działka", "przeznaczenie"],
  organ: "Wójt / Burmistrz / Prezydent Miasta",
  legalBasis:
    "art. 30 ust. 1 ustawy z dnia 27 marca 2003 r. o planowaniu i zagospodarowaniu przestrzennym; § 8 rozporządzenia Ministra Rozwoju i Technologii z dnia 8 grudnia 2023 r.",
  request:
    "Na podstawie art. 30 ust. 1 ustawy z dnia 27 marca 2003 r. o planowaniu i zagospodarowaniu przestrzennym wnoszę o wydanie dokumentów wskazanych poniżej dla nieruchomości opisanej we wniosku.",
  feeLabel: "od 20 zł",
  fee: "Wypis: do 5 stron 30 zł, powyżej 5 stron 50 zł; wyrys: 20 zł za każdą stronę A4 (najwyżej 200 zł). Opłatę urząd nalicza po przygotowaniu dokumentów.",
  deadlineLabel: "do 30 dni",
  deadline: "Do 30 dni (zwykle 7–14 dni).",
  sections: [
    applicantSection(),
    representationSection(),
    {
      code: "dokument",
      title: "Wnoszę o wydanie",
      fields: [
        {
          code: "zakres",
          type: "radio",
          label: "Dokument",
          required: true,
          defaultValue: "wypisWyrys",
          options: [
            { value: "wypisWyrys", label: "wypisu i wyrysu" },
            { value: "wypis", label: "wypisu" },
            { value: "wyrys", label: "wyrysu" },
          ],
        },
        {
          code: "plan",
          type: "radio",
          label: "Z dokumentu planistycznego",
          required: true,
          defaultValue: "mpzp",
          options: [
            { value: "mpzp", label: "miejscowego planu zagospodarowania przestrzennego" },
            { value: "planOgolny", label: "planu ogólnego gminy" },
          ],
        },
        { code: "egzemplarze", type: "number", label: "Liczba egzemplarzy", required: true, min: 1, max: 10, defaultValue: "1", width: "third" },
        { code: "cel", type: "text", label: "Cel wydania (nieobowiązkowo)", width: "twoThirds", maxLength: 200, placeholder: "np. sprzedaż nieruchomości" },
      ],
    },
    {
      code: "nieruchomosc",
      title: "Dane identyfikacyjne nieruchomości",
      description:
        "Wypis i wyrys dla kilku działek w ramach jednego wniosku i jednej opłaty jest możliwy, gdy działki leżą w bezpośrednim sąsiedztwie.",
      fields: [
        { code: "ulica", type: "text", label: "Miejscowość i ulica, przy której leży nieruchomość", maxLength: 200 },
        {
          code: "dzialki",
          type: "repeat",
          label: "Działki ewidencyjne",
          itemLabel: "Działka",
          required: true,
          min: 1,
          max: 20,
          fields: [
            { code: "obreb", type: "text", label: "Obręb (nazwa lub numer)", required: true, width: "half", maxLength: 100 },
            { code: "numer", type: "text", label: "Numer działki", required: true, width: "half", maxLength: 50, placeholder: "np. 125/4" },
          ],
        },
        { code: "czescDzialki", type: "checkbox", label: "Wniosek dotyczy części działki albo wielu działek — dołączę mapę z zaznaczonym terenem" },
      ],
    },
    {
      code: "oplata",
      title: "Opłata skarbowa",
      fields: [
        feeInfo(
          "infoOplata",
          "Od wypisu: do 5 stron — 30 zł, powyżej 5 stron — 50 zł. Od wyrysu: 20 zł za każdą pełną lub rozpoczętą część odpowiadającą stronie formatu A4 (nie więcej niż 200 zł). Opłata skarbowa zostanie naliczona po przygotowaniu wypisu i wyrysu — urząd poinformuje o kwocie przed wydaniem dokumentów.",
        ),
      ],
    },
    replySection(),
  ],
  attachments: [
    {
      code: "mapa",
      label: "Kopia mapy ewidencyjnej z zaznaczonym terenem",
      hint: "Gdy wniosek dotyczy części działki albo wielu działek.",
      visibleWhen: { field: "czescDzialki", equals: true },
      required: true,
    },
    ...proxyAttachments(),
    {
      code: "oplata",
      label: "Dowód uiszczenia opłaty skarbowej",
      hint: "Jeśli opłata została już wniesiona — inaczej urząd poda kwotę po przygotowaniu dokumentów.",
    },
    otherAttachments(),
  ],
};

export default A2;
